import React, { useMemo, useState } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    Divider,
    Tab,
    Tabs,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.herbieterms.com';

function NewsletterReviewInner() {
    const navigate = useNavigate();

    const [sourceText, setSourceText] = useState('');
    const [productName, setProductName] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [result, setResult] = useState(null); // response from /newsletter/translate-review
    const [tab, setTab] = useState('en');

    // Termbase add form (manual / assisted)
    const [newProductName, setNewProductName] = useState('');
    const [newEn, setNewEn] = useState('');
    const [newDa, setNewDa] = useState('');
    const [newNo, setNewNo] = useState('');
    const [newSv, setNewSv] = useState('');
    const [newFi, setNewFi] = useState('');
    const [newEs, setNewEs] = useState('');
    const [newPt, setNewPt] = useState('');
    const [addTermMsg, setAddTermMsg] = useState('');

    const tabs = useMemo(() => ([
        { key: 'en', label: 'English (Review)' },
        { key: 'da', label: 'Danish' },
        { key: 'no', label: 'Norwegian' },
        { key: 'sv', label: 'Swedish' },
        { key: 'fi', label: 'Finnish' },
        { key: 'es', label: 'Spanish' },
        { key: 'pt', label: 'Portuguese' },
    ]), []);

    const handleRun = async () => {
        setErrorMessage('');
        setAddTermMsg('');
        setResult(null);

        if (!sourceText.trim()) {
            setErrorMessage('Please paste English newsletter text first.');
            return;
        }

        setLoading(true);
        try {
            const resp = await axios.post(
                `${API_BASE}/newsletter/translate-review`,
                {
                    sourceText,
                    productName: productName.trim() ? productName.trim() : null,
                },
                {
                    withCredentials: true,
                    headers: { 'x-api-key': process.env.REACT_APP_API_KEY },
                }
            );
            setResult(resp.data);
            setTab('en');

            // Pre-fill term add form with “first found” term if you want,
            // but we keep it empty by default to avoid accidental inserts.
        } catch (e) {
            console.error(e);
            setErrorMessage(e?.response?.data?.message || 'Failed to generate translation/review. Check server logs.');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text || '');
        } catch (e) {
            console.error(e);
        }
    };

    const getScores = (langKey) => result?.reviews?.[langKey]?.scores || null;

    const getIssues = (langKey) => result?.reviews?.[langKey]?.issues || [];

    const getImprovedText = (langKey) => result?.reviews?.[langKey]?.improvedText || '';

    const getTranslation = (langKey) => result?.translations?.[langKey] || '';

    const handleAddTerm = async () => {
        setAddTermMsg('');

        const pn = newProductName.trim();
        if (!pn) {
            setAddTermMsg('Product name is required to add a term (matches your current Mongo schema).');
            return;
        }
        if (!newEn.trim()) {
            setAddTermMsg('English term is required.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                productName: pn,
                translations: {
                    en: newEn.trim(),
                    da: newDa.trim(),
                    no: newNo.trim(),
                    sv: newSv.trim(),
                    fi: newFi.trim(),
                    es: newEs.trim(),
                    pt: newPt.trim(),
                }
            };

            await axios.post(`${API_BASE}/terms`, payload, {
                withCredentials: true,
                headers: { 'x-api-key': process.env.REACT_APP_API_KEY },
            });
            setAddTermMsg('Term added to termbase successfully.');

            // Clear fields
            setNewProductName('');
            setNewEn('');
            setNewDa('');
            setNewNo('');
            setNewSv('');
            setNewFi('');
            setNewEs('');
            setNewPt('');
        } catch (e) {
            console.error(e);
            setAddTermMsg(e?.response?.data?.message || 'Failed to add term. Check server logs.');
        } finally {
            setLoading(false);
        }
    };

    const handlePrefillFromTextSelection = () => {
        // lightweight helper: prefill EN with current selection if user highlights text
        const selection = window.getSelection ? String(window.getSelection()) : '';
        if (selection && selection.trim()) {
            setNewEn(selection.trim());
            setAddTermMsg('Prefilled English term from selected text. Fill translations and click “Add to termbase”.');
        }
    };

    return (
        <Box sx={{ padding: '2rem' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4" fontWeight="bold">Newsletter Review</Typography>
                <Box display="flex" gap={1}>
                    <Button variant="outlined" onClick={() => navigate('/')}>Back to Home</Button>
                    <Button variant="outlined" onClick={() => navigate('/newsletter-compare')}>Compare versions</Button>
                </Box>
            </Box>

            <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Input</Typography>

                <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                    <TextField
                        label="Product context (optional)"
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        fullWidth
                    />
                </Box>

                <TextField
                    label="English newsletter text"
                    value={sourceText}
                    onChange={(e) => setSourceText(e.target.value)}
                    fullWidth
                    multiline
                    minRows={8}
                />

                <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                    <Button variant="contained" onClick={handleRun} disabled={loading}>
                        {loading ? 'Working…' : 'Translate & Review'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setSourceText('');
                            setProductName('');
                            setResult(null);
                            setErrorMessage('');
                            setAddTermMsg('');
                        }}
                        disabled={loading}
                    >
                        Clear
                    </Button>
                </Box>

                {errorMessage && (
                    <Typography mt={2} color="error">{errorMessage}</Typography>
                )}
            </Card>

            {result && (
                <>
                    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>Results</Typography>

                        <Tabs
                            value={tab}
                            onChange={(e, v) => setTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ mb: 2 }}
                        >
                            {tabs.map(t => <Tab key={t.key} value={t.key} label={t.label} />)}
                        </Tabs>

                        {/* Translation text */}
                        {tab !== 'en' && (
                            <>
                                <Typography variant="subtitle1" fontWeight="bold">Translation</Typography>
                                <Card variant="outlined" sx={{ p: 2, mt: 1, mb: 1, whiteSpace: 'pre-wrap' }}>
                                    <Typography>{getTranslation(tab)}</Typography>
                                </Card>
                                <Button
                                    variant="outlined"
                                    onClick={() => copyToClipboard(getTranslation(tab))}
                                >
                                    Copy translation
                                </Button>
                                <Divider sx={{ my: 2 }} />
                            </>
                        )}

                        {/* Improved text for en/da/no/sv */}
                        {tab !== 'fi' && (
                            <>
                                <Typography variant="subtitle1" fontWeight="bold">Improved version</Typography>
                                <Card variant="outlined" sx={{ p: 2, mt: 1, mb: 1, whiteSpace: 'pre-wrap' }}>
                                    <Typography>{getImprovedText(tab)}</Typography>
                                </Card>
                                <Button
                                    variant="outlined"
                                    onClick={() => copyToClipboard(getImprovedText(tab))}
                                >
                                    Copy improved version
                                </Button>
                                <Divider sx={{ my: 2 }} />
                            </>
                        )}

                        {/* Scores */}
                        <Typography variant="subtitle1" fontWeight="bold">Scores</Typography>
                        {getScores(tab) ? (
                            <Table size="small" sx={{ mt: 1, mb: 2 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Dimension</TableCell>
                                        <TableCell>Score</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(getScores(tab)).map(([k, v]) => (
                                        <TableRow key={k}>
                                            <TableCell>{k}</TableCell>
                                            <TableCell>{v}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography sx={{ mt: 1 }}>No scores available.</Typography>
                        )}

                        {/* Issues (not for fi) */}
                        {tab !== 'fi' && (
                            <>
                                <Typography variant="subtitle1" fontWeight="bold">Issues & suggestions</Typography>
                                {getIssues(tab).length > 0 ? (
                                    <Table size="small" sx={{ mt: 1 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Severity</TableCell>
                                                <TableCell>Category</TableCell>
                                                <TableCell>Quote</TableCell>
                                                <TableCell>Suggestion</TableCell>
                                                <TableCell>Explanation</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {getIssues(tab).map((iss, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{iss.severity}</TableCell>
                                                    <TableCell>{iss.category}</TableCell>
                                                    <TableCell>{iss.quote}</TableCell>
                                                    <TableCell>{iss.suggestion}</TableCell>
                                                    <TableCell>{iss.explanation}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Typography sx={{ mt: 1 }}>No issues found.</Typography>
                                )}
                            </>
                        )}
                    </Card>

                    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>Terminology findings</Typography>

                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
                            Found glossary terms in source
                        </Typography>
                        {result?.terminology?.foundTerms?.length ? (
                            <Table size="small" sx={{ mt: 1, mb: 2 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Term ID</TableCell>
                                        <TableCell>English</TableCell>
                                        <TableCell>Product</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {result.terminology.foundTerms.map((t, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{t.termId}</TableCell>
                                            <TableCell>{t.en}</TableCell>
                                            <TableCell>{t.productName}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography sx={{ mt: 1 }}>No glossary terms detected.</Typography>
                        )}

                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
                            Terminology violations
                        </Typography>
                        {result?.terminology?.violations?.length ? (
                            <Table size="small" sx={{ mt: 1 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Language</TableCell>
                                        <TableCell>Term ID</TableCell>
                                        <TableCell>Expected</TableCell>
                                        <TableCell>Found</TableCell>
                                        <TableCell>Explanation</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {result.terminology.violations.map((v, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{v.language}</TableCell>
                                            <TableCell>{v.termId}</TableCell>
                                            <TableCell>{v.expected}</TableCell>
                                            <TableCell>{v.found}</TableCell>
                                            <TableCell>{v.explanation}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography sx={{ mt: 1 }}>No terminology violations detected.</Typography>
                        )}
                    </Card>
                </>
            )}

            <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Add a term to the Mongo termbase</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Tip: highlight a phrase in the newsletter text, then click “Prefill from selection”.
                </Typography>

                <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    <Button variant="outlined" onClick={handlePrefillFromTextSelection}>
                        Prefill from selection
                    </Button>
                </Box>

                <Box display="flex" gap={2} flexWrap="wrap">
                    <TextField
                        label="Product name (required)"
                        value={newProductName}
                        onChange={(e) => setNewProductName(e.target.value)}
                        sx={{ flex: '1 1 260px' }}
                    />
                    <TextField
                        label="English (required)"
                        value={newEn}
                        onChange={(e) => setNewEn(e.target.value)}
                        sx={{ flex: '1 1 260px' }}
                    />
                    <TextField
                        label="Danish"
                        value={newDa}
                        onChange={(e) => setNewDa(e.target.value)}
                        sx={{ flex: '1 1 260px' }}
                    />
                    <TextField
                        label="Norwegian"
                        value={newNo}
                        onChange={(e) => setNewNo(e.target.value)}
                        sx={{ flex: '1 1 260px' }}
                    />
                    <TextField
                        label="Swedish"
                        value={newSv}
                        onChange={(e) => setNewSv(e.target.value)}
                        sx={{ flex: '1 1 260px' }}
                    />
                    <TextField
                        label="Finnish"
                        value={newFi}
                        onChange={(e) => setNewFi(e.target.value)}
                        sx={{ flex: '1 1 260px' }}
                    />
                    <TextField
                        label="Spanish"
                        value={newEs}
                        onChange={(e) => setNewEs(e.target.value)}
                        sx={{ flex: '1 1 260px' }}
                    />
                    <TextField
                        label="Portuguese"
                        value={newPt}
                        onChange={(e) => setNewPt(e.target.value)}
                        sx={{ flex: '1 1 260px' }}
                    />
                </Box>

                <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                    <Button variant="contained" onClick={handleAddTerm} disabled={loading}>
                        Add to termbase
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setNewProductName('');
                            setNewEn('');
                            setNewDa('');
                            setNewNo('');
                            setNewSv('');
                            setNewFi('');
                            setNewEs('');
                            setNewPt('');
                            setAddTermMsg('');
                        }}
                        disabled={loading}
                    >
                        Clear term form
                    </Button>
                </Box>

                {addTermMsg && (
                    <Typography mt={2} color={addTermMsg.toLowerCase().includes('success') ? 'success.main' : 'error'}>
                        {addTermMsg}
                    </Typography>
                )}
            </Card>
        </Box>
    );
}

export default function NewsletterReview() {
    return (
        <Authenticator>
            {({ signOut }) => (
                <Box>
                    <NewsletterReviewInner />
                    <Box sx={{ p: 2 }}>
                        <Button onClick={signOut}>Sign out</Button>
                    </Box>
                </Box>
            )}
        </Authenticator>
    );
}
